import { Alert, Button, Card, Form } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import isEmail from 'validator/es/lib/isEmail'
import ReCAPTCHA from 'react-google-recaptcha'

import { useCheckEmailExistsMutation, useCreateAccountMutation } from '../../app/authApiSlice'
import ControlInput from '../../components/form-inputs/SignupControlInput'
import { isValidPassword } from '../../utils/validateInput'
import LoadingTextSwap from '../../components/LoadingTextSwap'

const CreateAccount = () => {
  const navigate = useNavigate()

  const [checkEmailExists, { isError: isEmailError, isLoading: emailLoading }] = useCheckEmailExistsMutation()

  const [createAccount, { isLoading: createAccountLoading, isError: isCreateAccountError }] = useCreateAccountMutation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [recaptchaToken, setRecaptchaToken] = useState(null)

  const [valid, setValid] = useState({
    email: false,
    password: false,
    passwordConfirm: false,
  })

  const allValid = Object.values(valid).every(Boolean) && Boolean(recaptchaToken)

  // Function to update valid state
  const updateValid = (key, value) => {
    setValid(prev => ({ ...prev, [key]: value }))
  }

  const [error, setError] = useState('')
  const [emailChecked, setEmailChecked] = useState(false)
  const [emailAlreadyExists, setEmailAlreadyExists] = useState(false)
  const [blurAll, setBlurAll] = useState(false)

  const isLoading = emailLoading || createAccountLoading

  const handleSetEmail = value => {
    setEmail(value)
    emailChecked && setEmailChecked(false)
    setEmailAlreadyExists(false)
  }

  // Special handler for password to also validate passwordConfirm if it has a value
  const handleSetPassword = value => {
    setPassword(value)
    if (value !== passwordConfirm) {
      updateValid('passwordConfirm', false)
    } else {
      updateValid('passwordConfirm', true)
    }
  }

  const handleRecaptchaChange = token => {
    setRecaptchaToken(token)
    // Clear any existing error when reCAPTCHA is completed
    if (error === 'Please complete the reCAPTCHA verification') {
      setError('')
    }
  }

  const handleCheckEmailExists = async () => {
    if (!emailLoading) {
      try {
        const response = await checkEmailExists(email).unwrap()

        setEmailChecked(true)
        setEmailAlreadyExists(response.emailExists)
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleCreateAccount = async () => {
    if (!allValid) return

    try {
      await createAccount({
        email,
        password,
        recaptchaToken,
      }).unwrap()
    } catch (error) {
      console.log(error)
    }
  }

  const handleSubmit = e => {
    e.preventDefault()

    if (!valid.email) return

    if (!emailChecked) {
      handleCheckEmailExists()
    } else if (emailAlreadyExists) {
      navigate('/login')
    } else {
      if (allValid) handleCreateAccount()
    }
  }

  const passwordInputs = (
    <>
      <ControlInput
        label="Password"
        type="password"
        name="password"
        value={password}
        isValid={valid.password}
        setValid={updateValid}
        setData={handleSetPassword}
        validTest={isValidPassword}
        blurAll={blurAll}
        feedback="Password must be at least 8 characters long and contain at least one number, one uppercase letter, one lowercase letter and one special character"
      />

      <ControlInput
        label="Confirm Password"
        type="password"
        name="passwordConfirm"
        value={passwordConfirm}
        isValid={valid.passwordConfirm}
        setValid={updateValid}
        setData={setPasswordConfirm}
        blurAll={blurAll}
        validTest={newPasswordConfirm => password === newPasswordConfirm}
        feedback="Passwords do not match"
      />

      <div className="mt-4 d-flex justify-content-center">
        <ReCAPTCHA sitekey="6Ld3rGMrAAAAACx5MrNnvegVrwl_dTnFc-vA9wbZ" onChange={handleRecaptchaChange} />
      </div>
    </>
  )

  return (
    <>
      <Card className="w-100 border-0 shadow" style={{ maxWidth: '400px' }}>
        <Card.Body>
          <h2 className="text-center mb-3">Create Account</h2>

          <p className='text-center'>
            Create your account to join Gymnastic Britannia and access exclusive member benefits, training resources,
            and our community network.
          </p>

          {isEmailError && <Alert variant="danger">Failed to verify email. Please try again...</Alert>}
          {isCreateAccountError && (
            <Alert variant="danger">Failed to create account. Please refresh and try again...</Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <ControlInput
              required
              name="email"
              label="Email"
              type="email"
              value={email}
              isValid={valid.email}
              setValid={updateValid}
              setData={handleSetEmail}
              validTest={isEmail}
              feedback="Please enter a valid email address"
              blurAll={blurAll}
            />

            {/* Allow create account if email checked and not already in database */}
            {emailChecked && !emailAlreadyExists && passwordInputs}

            {emailAlreadyExists && (
              <p className="text-danger mb-0 fs-8 i fw-semibold">Email already exists. Try logging in.</p>
            )}
            {error && <p className="text-danger mb-0 fs-8 i fw-semibold">{error}</p>}
            <Button
              disabled={isLoading || (emailChecked && !emailAlreadyExists && !allValid)}
              className="w-100 mt-4"
              type="submit"
            >
              <LoadingTextSwap isLoading={isLoading}>{emailAlreadyExists ? 'Go to Login' : 'Continue'}</LoadingTextSwap>
            </Button>
          </Form>
          <div className="w-100 text-center mt-3">
            <Link to="/login/forgot-password">Forgot Password?</Link>
          </div>
        </Card.Body>
      </Card>
      <div className="w-100 text-center mt-2">
        Already have an account? <Link to="/login">Log In</Link>
      </div>
    </>
  )
}
export default CreateAccount
